import {
  CompaniesCountTotalDataTransformer,
  DashboardDataTransformer,
  IndexValueFormatDataTransformer,
  KEuroToEuroValueDataTransformer,
  PercentageValueDataTransformer,
  RankDashboardDataTransformer,
} from '../../../../data_transformers/dashboard_data_transformer';
import { DashboardFilter } from '../../../../filters/dashboard_filter';
import {
  DashboardConfigurationDependencies,
  Metric,
} from '../../../../dashboard-config/dashboard-config.model';
import {
  KpiColumnDataTransformer,
  KpiLabelDataTransformer,
} from '../../../../dashboard-config/kpis/kpi-data-transformers';
import {
  IndexIndicatorColumnDataTransformer,
  IndexIndicatorLabelDataTransformer,
  INDICATOR_FILTER_NAME,
} from '../../../../dashboard-config/kpis/index-indicators';
import {
  companiesRevenueProfile,
  economicParticipantsProfile,
  employedProfile,
  gvaProfile,
} from '../../../../dashboard-config/metrics/metric-profile-implementations';
import { MetricProfile } from '../../../../dashboard-config/metrics/metric-profile';
import {
  createIndexFilters,
  INDEX_GVA_SUB_MARKET_WZ_ONLY_OPTIONS,
} from '../index-metric.profile';
import { IndexRankingPopupView } from './dashboard-index-ranking.definition';
import { DashboardIndexRankingShell } from './dashboard-index-ranking-shell.profile';

export interface IndexMetricProfile {
  metricId: Metric;
  /** Shared metric profile: KPIs, labels, temporal kind. */
  base: MetricProfile;
  headingMetricValue: (filterValues: Map<string, any>) => string;
  headingTimeValue: (filterValues: Map<string, any>) => string;
  /** Optional full overrides (e.g. employed × districts). */
  chartHeading?: (
    filterValues: Map<string, any>,
    shell: DashboardIndexRankingShell,
  ) => string | undefined;
  mapHeading?: (
    filterValues: Map<string, any>,
    shell: DashboardIndexRankingShell,
  ) => string | undefined;
  popup: IndexRankingPopupView;
  createMetricFilters: (
    shell: DashboardIndexRankingShell,
  ) => DashboardFilter<any>[];
  createChartValueTransformers: (
    valueKey: string,
    shell: DashboardIndexRankingShell,
  ) => DashboardDataTransformer[];
  createIndexValueDisplayTransformers: () => DashboardDataTransformer[];
  createMetricValueDisplayTransformers: (
    valueKey: string,
  ) => DashboardDataTransformer[];
  createMetricLabelTransformers: () => DashboardDataTransformer[];
  createRankTransformers: (valueKey: string) => DashboardDataTransformer[];
  createExportTransformers: (
    shell: DashboardIndexRankingShell,
  ) => DashboardDataTransformer[];
  createPreFilterExtras?: (
    deps: DashboardConfigurationDependencies,
  ) => DashboardDataTransformer[];
}

function withCompaniesCountTotal(
  extras?: (
    deps: DashboardConfigurationDependencies,
  ) => DashboardDataTransformer[],
): (deps: DashboardConfigurationDependencies) => DashboardDataTransformer[] {
  return (deps) => [
    new CompaniesCountTotalDataTransformer(),
    ...(extras?.(deps) ?? []),
  ];
}

function employedDistrictsHeadingLabel(filterValues: Map<string, any>): string {
  return filterValues.get(INDICATOR_FILTER_NAME)?.includes('GESAMTBESCHÄFTIGUNG')
    ? 'GESAMTBESCHÄFTIGUNG'
    : 'BEVÖLKERUNG';
}

/** Employed value: the selected type × indicator, plus the state-level count. */
function employedStatesChartValueTransformers(
  valueKey: string,
): DashboardDataTransformer[] {
  return [
    new IndexIndicatorColumnDataTransformer({ resultColumn: valueKey }),
    new KpiColumnDataTransformer({ measure: 'stateValue', resultColumn: 'employed_count_state' }),
  ];
}

/** Kreise: the type filter is hidden (Gesamtbeschäftigte), so type × indicator reads the total. */
function employedDistrictsChartValueTransformers(
  valueKey: string,
): DashboardDataTransformer[] {
  return [new IndexIndicatorColumnDataTransformer({ resultColumn: valueKey })];
}

export const indexCompaniesRevenueProfile: IndexMetricProfile = {
  metricId: Metric.CompaniesRevenue,
  base: companiesRevenueProfile,
  headingMetricValue: (fv) => fv.get(INDICATOR_FILTER_NAME),
  headingTimeValue: (fv) => fv.get('year'),
  popup: { metricId: Metric.CompaniesRevenue, slot: 'revenue' },
  createMetricFilters: (shell) =>
    createIndexFilters(companiesRevenueProfile, {
      type: 'none',
      excludedIndicators: shell === 'districts' ? ['revenue_per_small_company__k_eur'] : [],
    }),
  createChartValueTransformers: (valueKey) => [
    new IndexIndicatorColumnDataTransformer({ resultColumn: valueKey }),
    new IndexIndicatorColumnDataTransformer({
      resultColumn: valueKey + '_companies_count',
      source: { impliedMetric: Metric.CompaniesCount },
    }),
    new IndexIndicatorColumnDataTransformer({
      resultColumn: valueKey + '_companies_sales',
      source: { impliedMetric: Metric.CompaniesRevenue },
    }),
  ],
  createIndexValueDisplayTransformers: () => [new IndexIndicatorLabelDataTransformer()],
  createMetricValueDisplayTransformers: (valueKey) => [
    new KEuroToEuroValueDataTransformer(valueKey, {
      resultColumnKey: valueKey + '_display',
      addEur: true,
    }),
  ],
  createMetricLabelTransformers: () => [],
  createRankTransformers: (valueKey) => [
    new RankDashboardDataTransformer(
      'companies_revenue_rank',
      'companies_revenue__k_eur',
    ),
    new RankDashboardDataTransformer(
      'small_companies_revenue_rank',
      'small_companies_revenue__k_eur',
    ),
    new RankDashboardDataTransformer('rank', valueKey),
  ],
  createExportTransformers: () => [
    new KEuroToEuroValueDataTransformer('companies_revenue__k_eur'),
    new KEuroToEuroValueDataTransformer('small_companies_revenue__k_eur'),
  ],
  createPreFilterExtras: withCompaniesCountTotal(),
};

export const indexGrossValueAddedProfile: IndexMetricProfile = {
  metricId: Metric.GrossValueAdded,
  base: gvaProfile,
  headingMetricValue: (fv) => fv.get(INDICATOR_FILTER_NAME),
  headingTimeValue: (fv) => fv.get('year'),
  popup: { metricId: Metric.GrossValueAdded, slot: 'gva' },
  createMetricFilters: () =>
    createIndexFilters(gvaProfile, { type: 'none', excludedIndicators: INDEX_GVA_SUB_MARKET_WZ_ONLY_OPTIONS }),
  createChartValueTransformers: (valueKey) => [
    new IndexIndicatorColumnDataTransformer({ resultColumn: valueKey }),
  ],
  createIndexValueDisplayTransformers: () => [],
  createMetricValueDisplayTransformers: (valueKey) => [
    new KEuroToEuroValueDataTransformer(valueKey, {
      resultColumnKey: valueKey + '_display',
      addEur: true,
    }),
  ],
  createMetricLabelTransformers: () => [],
  createRankTransformers: (valueKey) => [
    new RankDashboardDataTransformer(
      'gross_value_added_rank',
      'gross_value_added__k_eur',
    ),
    new RankDashboardDataTransformer('rank', valueKey),
  ],
  createExportTransformers: () => [
    new KEuroToEuroValueDataTransformer('gross_value_added__k_eur'),
    new KEuroToEuroValueDataTransformer(
      'gross_value_added_per_total_company__k_eur',
    ),
    new KEuroToEuroValueDataTransformer(
      'gross_value_added_per_economic_participants_total__k_eur',
    ),
    new KEuroToEuroValueDataTransformer(
      'gross_value_added_per_employed_total__k_eur',
    ),
  ],
  createPreFilterExtras: withCompaniesCountTotal(),
};

export const indexEconomicParticipantProfile: IndexMetricProfile = {
  metricId: Metric.EconomicParticipant,
  base: economicParticipantsProfile,
  headingMetricValue: (fv) => fv.get(INDICATOR_FILTER_NAME),
  headingTimeValue: (fv) => fv.get('year'),
  popup: {
    metricId: Metric.EconomicParticipant,
    slot: 'economic_participants',
  },
  createMetricFilters: () => createIndexFilters(economicParticipantsProfile, { type: 'hidden' }),
  createChartValueTransformers: (valueKey) => [
    new IndexIndicatorColumnDataTransformer({ resultColumn: valueKey }),
  ],
  createIndexValueDisplayTransformers: () => [],
  createMetricValueDisplayTransformers: (valueKey) => [
    new IndexValueFormatDataTransformer({
      filterName: INDICATOR_FILTER_NAME,
      valueColumnKey: valueKey,
      resultColumnKey: valueKey + '_display',
    }),
  ],
  createMetricLabelTransformers: () => [new KpiLabelDataTransformer()],
  createRankTransformers: (valueKey) => [
    new RankDashboardDataTransformer(
      'economic_participants_total_rank',
      'economic_participants_total',
    ),
    new RankDashboardDataTransformer(
      'economic_participants_core_market_rank',
      'economic_participants_core_market',
    ),
    new RankDashboardDataTransformer(
      'economic_participants_marginal_rank',
      'economic_participants_marginal',
    ),
    new RankDashboardDataTransformer('rank', valueKey),
  ],
  createExportTransformers: () => [
    new KEuroToEuroValueDataTransformer('companies_revenue__k_eur'),
  ],
  createPreFilterExtras: withCompaniesCountTotal(),
};

export const indexEmployedProfile: IndexMetricProfile = {
  metricId: Metric.Employed,
  base: employedProfile,
  headingMetricValue: (fv) => fv.get(INDICATOR_FILTER_NAME),
  headingTimeValue: (fv) => fv.get('year'),
  chartHeading: (fv, shell) => {
    if (shell !== 'districts') {
      return undefined;
    }
    const employedIndex = employedDistrictsHeadingLabel(fv);
    return `RANKING: TOP ${fv.get('districts').length}-KREISE NACH ANTEIL TEILMARKTBESCHÄFTIGTER AN ${employedIndex}, ${fv.get('year')}`;
  },
  mapHeading: (fv, shell) => {
    if (shell !== 'districts') {
      return undefined;
    }
    return `ANTEIL TEILMARKTBESCHÄFTIGTER AN ${employedDistrictsHeadingLabel(fv)}`;
  },
  popup: { metricId: Metric.Employed, slot: 'employed' },
  createMetricFilters: (shell) =>
    shell === 'districts'
      ? createIndexFilters(employedProfile, {
          type: 'hidden',
          excludedIndicators: ['employed_per_company', 'employed_state_pct'],
        })
      : createIndexFilters(employedProfile, { type: 'visible', excludedIndicators: ['employed_district_pct'] }),
  // WZ-Klassen × Bundesländer maps no value (legacy; that dashboard is disabled in the menu).
  createChartValueTransformers: (valueKey, shell) => [
    new KpiColumnDataTransformer({ measure: 'value', resultColumn: 'employed_count' }),
    ...(shell === 'states'
      ? employedStatesChartValueTransformers(valueKey)
      : []),
    ...(shell === 'districts'
      ? employedDistrictsChartValueTransformers(valueKey)
      : []),
  ],
  createIndexValueDisplayTransformers: () => [new IndexIndicatorLabelDataTransformer()],
  createMetricValueDisplayTransformers: (valueKey) => [
    new IndexValueFormatDataTransformer({
      filterName: INDICATOR_FILTER_NAME,
      valueColumnKey: valueKey,
      resultColumnKey: valueKey + '_display',
    }),
  ],
  createMetricLabelTransformers: () => [new KpiLabelDataTransformer()],
  createRankTransformers: (valueKey) => [
    new RankDashboardDataTransformer('employed_total_rank', 'employed_total'),
    new RankDashboardDataTransformer(
      'employed_social_security_rank',
      'employed_social_security',
    ),
    new RankDashboardDataTransformer(
      'employed_marginal_rank',
      'employed_marginal',
    ),
    new RankDashboardDataTransformer('rank', valueKey),
  ],
  createExportTransformers: (shell) => {
    const transformers: DashboardDataTransformer[] = [
      new PercentageValueDataTransformer('employed_total_population_pct'),
    ];
    if (shell === 'states') {
      transformers.push(
        new PercentageValueDataTransformer('employed_total_state_pct'),
      );
    } else if (shell === 'districts') {
      transformers.push(
        new PercentageValueDataTransformer('employed_total_district_pct'),
      );
    }
    return transformers;
  },
  createPreFilterExtras: withCompaniesCountTotal(),
};
